import { useEffect } from 'react';
import type { CSSProperties } from 'react';

const BYNDER_SCRIPT_ID = 'bynder-widgets-js';
const BYNDER_SCRIPT_SRC =
  'https://d8ejoa1fys2rk.cloudfront.net/bynder-embed/latest/bynder-embed.js';
const BYNDER_ACCOUNT_URL = 'assets.adspipe.com';

declare global {
  interface Window {
    BynderWidgets?: { init?: () => void };
  }
}

/**
 * Renders a Bynder-hosted video widget. The Bynder embed script scans the DOM
 * for `[data-bynder-widget]` elements; we ensure the script is loaded once and
 * (re-)initialise so widgets mounted after the initial load still render.
 */
export function BynderVideo({
  mediaId,
  className,
  style,
}: {
  mediaId: string;
  className?: string;
  style?: CSSProperties;
}) {
  useEffect(() => {
    const initialize = () => window.BynderWidgets?.init?.();
    const existingScript = document.getElementById(BYNDER_SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      initialize();
      existingScript.addEventListener('load', initialize, { once: true });
      return () => existingScript.removeEventListener('load', initialize);
    }

    const script = document.createElement('script');
    script.id = BYNDER_SCRIPT_ID;
    script.src = BYNDER_SCRIPT_SRC;
    script.async = true;
    script.setAttribute('data-account-url', BYNDER_ACCOUNT_URL);
    script.setAttribute('data-language', 'en');
    script.addEventListener('load', initialize, { once: true });
    document.body.appendChild(script);

    return () => script.removeEventListener('load', initialize);
  }, [mediaId]);

  return (
    <div
      className={className}
      style={style}
      data-bynder-widget="video-item"
      data-media-id={mediaId}
    />
  );
}
